import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import Input from 'components/Input'
import Textarea from 'components/Textarea'
import { delay } from 'utils/delay'
import { requestUpdateSiteSpecieAssesmentParameter } from 'api/siteSpecieAssesmentParameter'
import { isValidDecimalNumber, noZeroAfterDecimal } from 'utils/isValidDecimalNumber'
import { SiteContext } from 'providers/site'
import { arrIdCriteria, getIdCriteria, D1b_Value } from './constants'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { minBestMaxValidation } from 'utils/minBestMaxValidation'
import toNumberOrNull from 'utils/toNumberOrNull'
import { SpeciesDetailsContainer, SpeciesDetailsItemWrapper } from '../style'
import { NUMBER_OF_MATURE_INDIVIDUALS_ID, NUMBER_OF_LOCALITIES_ID } from 'constants/speciesParameterType'

const fields = {
  min: 'minGlobal',
  best: 'bestGlobal',
  max: 'maxGlobal'
}

const inputSaveDelayTime = 300
const updateSourceTimeOut = delay(inputSaveDelayTime)
const updateNotesTimeOut = delay(inputSaveDelayTime)
const emptyFieldError = { field: '', error: '' }

function isMatureIndividualsOrLocalitiesParameter(siteAssesmentParameterTypeId) {
  return (
    siteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
    siteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID
  )
}

function parseGlobalFieldNumericValue(raw, siteAssesmentParameterTypeId, mode) {
  const matureOrLocalities = isMatureIndividualsOrLocalitiesParameter(siteAssesmentParameterTypeId)
  if (mode === 'changeBestWhenDecimal') {
    const decimals = matureOrLocalities && raw.includes('.') ? 0 : 10
    return Number(Number.parseFloat(raw).toFixed(decimals))
  }
  const decimals = matureOrLocalities ? 0 : 10
  return Number(Number.parseFloat(raw).toFixed(decimals))
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== ''
}

const SpeciesParameterGlobalDataContent = ({
  parameter,
  siteSpecieParameterId,
  updateParameterList,
  species,
  qualified,
  assessmentParameter,
  siteBestGlobal,
  setSiteBestGlobal,
  siteJustification,
  setSiteJustification
}) => {
  const params = useParams()
  const siteHistoryIdParam = toNumberOrNull(params.siteHistoryId)
  const speciesIdNum = species.id
  const speciesHistoryIdNum = species.historyId
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const hasParameterType = hasValue(parameter?.SiteAssesmentParameterTypeId)
  const hasAssessmentParameterValue = hasValue(assessmentParameter?.value)
  const hasAnyAssessmentTypeValue = hasParameterType || hasAssessmentParameterValue
  const disabled = !hasAnyAssessmentTypeValue || site.SiteStatusId !== SITE_STATUS_DRAFT_ID || !isEditable || !qualified

  const [minGlobal, setMinGlobal] = useState(parameter.minGlobal || '')
  const [bestGlobal, setBestGlobal] = useState(parameter.bestGlobal || '')
  const [maxGlobal, setMaxGlobal] = useState(parameter.maxGlobal || '')
  const [source, setSource] = useState(parameter.sourceGlobalData || '')
  const [notes, setNotes] = useState(parameter.notes || '')
  const [error, setError] = useState({ field: '', error: '' })
  const siteNatureOfOccurance = getIdCriteria(parameter, arrIdCriteria).includes(D1b_Value)
  const isIbaOnly = parameter.iba_only === true

  const checkFields = siteProposalFieldValidation.assessmentParameters[parameter.id]
    ? siteProposalFieldValidation.assessmentParameters[parameter.id]
    : defaultSiteProposalValidationResults.assessmentParameters

  function shouldValidateField(fieldName) {
    if (qualified) {
      return checkFields[fieldName]
    }
    return true
  }

  function handleChangeMinGlobal(e) {
    const errorMessage = minBestMaxValidation(e.target.value, bestGlobal, maxGlobal, fields, 'min')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMinGlobal(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMinGlobal(e.target.value)
        setError(errorMessage)
      }
      const raw = e.target.value
      const parsedMinGlobal = parseGlobalFieldNumericValue(raw, parameter.SiteAssesmentParameterTypeId, 'change')
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        minGlobal: raw === '' ? null : parsedMinGlobal
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update minGlobal:', err)
      })
    }
  }

  function handleBlurMinGlobal(e) {
    if (isMatureIndividualsOrLocalitiesParameter(parameter.SiteAssesmentParameterTypeId) && e.target.value.includes('.')) {
      return
    }

    const errorMessage = minBestMaxValidation(e.target.value, bestGlobal, maxGlobal, fields, 'min')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMinGlobal(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMinGlobal(e.target.value)
        setError(errorMessage)
      }
      const raw = e.target.value
      const parsedMinGlobal = parseGlobalFieldNumericValue(raw, parameter.SiteAssesmentParameterTypeId, 'change')
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        minGlobal: raw === '' ? null : parsedMinGlobal
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update minGlobal on blur:', err)
      })
    }
  }

  function handleChangeBestGlobal(e) {
    const errorMessage = minBestMaxValidation(minGlobal, e.target.value, maxGlobal, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setBestGlobal(e.target.value)
        setSiteBestGlobal(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setBestGlobal(e.target.value)
        setSiteBestGlobal(e.target.value)
        setError(errorMessage)
      }
      const raw = e.target.value
      const parsedBestGlobal = parseGlobalFieldNumericValue(raw, parameter.SiteAssesmentParameterTypeId, 'changeBestWhenDecimal')
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        bestGlobal: raw === '' ? null : parsedBestGlobal
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update bestGlobal:', err)
      })
    }
  }

  function handleBlurBestGlobal(e) {
    if (isMatureIndividualsOrLocalitiesParameter(parameter.SiteAssesmentParameterTypeId) && e.target.value.includes('.')) {
      return
    }

    const errorMessage = minBestMaxValidation(minGlobal, e.target.value, maxGlobal, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setBestGlobal(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setBestGlobal(e.target.value)
        setError(errorMessage)
      }
      const raw = e.target.value
      const parsedBestGlobal = Number(Number.parseFloat(raw).toFixed(10))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        bestGlobal: raw === '' ? null : parsedBestGlobal
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update bestGlobal on blur:', err)
      })
    }
  }

  function handleChangeMaxGlobal(e) {
    const errorMessage = minBestMaxValidation(minGlobal, bestGlobal, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMaxGlobal(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMaxGlobal(e.target.value)
        setError(errorMessage)
      }
      const raw = e.target.value
      const parsedMaxGlobal = parseGlobalFieldNumericValue(raw, parameter.SiteAssesmentParameterTypeId, 'change')
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        maxGlobal: raw === '' ? null : parsedMaxGlobal
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update maxGlobal:', err)
      })
    }
  }

  function handleBlurMaxGlobal(e) {
    if (isMatureIndividualsOrLocalitiesParameter(parameter.SiteAssesmentParameterTypeId) && e.target.value.includes('.')) {
      return
    }

    const errorMessage = minBestMaxValidation(minGlobal, bestGlobal, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value)) && noZeroAfterDecimal(e.target.value)) {
        setMaxGlobal(e.target.value)
        setError(errorMessage)
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMaxGlobal(e.target.value)
        setError(errorMessage)
      }
      const raw = e.target.value
      const parsedMaxGlobal = Number(Number.parseFloat(raw).toFixed(10))
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        maxGlobal: raw === '' ? null : parsedMaxGlobal
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update maxGlobal on blur:', err)
      })
    }
  }

  function handleChangeSource(e) {
    setSource(e.target.value)
    updateSourceTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        sourceGlobalData: e.target.value
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update sourceGlobalData:', err)
      })
    })
  }

  function handleChangeNotes(e) {
    setNotes(e.target.value)
    updateNotesTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(siteSpecieParameterId, siteHistoryIdParam, speciesIdNum, speciesHistoryIdNum, {
        notes: e.target.value
      }).then(updateParameterList).catch((err) => {
        console.error('Failed to update notes:', err)
      })
    })
  }

  const minGlobalError = error.field === 'minGlobal' ? error : emptyFieldError
  const maxGlobalError = error.field === 'maxGlobal' ? error : emptyFieldError
  const canUseBestGlobalErrorState = isIbaOnly || shouldValidateField('bestGlobal')
  let bestGlobalError = { field: 'bestGlobal', error: 'pleaseCompleteThisField' }
  if (canUseBestGlobalErrorState) {
    bestGlobalError = error.field === 'bestGlobal' ? error : emptyFieldError
  }
  const sourceGlobalDataError =
    isIbaOnly || shouldValidateField('sourceGlobalData')
      ? emptyFieldError
      : { field: 'sourceGlobalData', error: 'pleaseCompleteThisField' }

  return (
    <SpeciesDetailsContainer noPadding={true}>
      <SpeciesDetailsItemWrapper>
        <Input
          value={minGlobal}
          onChange={handleChangeMinGlobal}
          onBlur={handleBlurMinGlobal}
          placeholder='enterSpeciesMinGlobalData'
          label='minGlobal'
          field='minGlobal'
          infoTooltip='minGlobalHint'
          zIndex='1070'
          disabled={disabled}
          isOptional={true}
          error={minGlobalError}
          errorMarginTop='8px'
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={bestGlobal}
          onChange={handleChangeBestGlobal}
          onBlur={handleBlurBestGlobal}
          placeholder='enterSpeciesBestGlobalData'
          label='bestGlobal'
          field='bestGlobal'
          infoTooltip='bestGlobalHint'
          zIndex='1070'
          isOptional={siteNatureOfOccurance || isIbaOnly}
          disabled={disabled}
          link='info@keybiodiversityareas.org'
          errorMarginTop='8px'
          error={bestGlobalError}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={maxGlobal}
          onChange={handleChangeMaxGlobal}
          onBlur={handleBlurMaxGlobal}
          placeholder='enterSpeciesMaxGlobalData'
          label='maxGlobal'
          field='maxGlobal'
          infoTooltip='maxGlobalHint'
          zIndex='1070'
          disabled={disabled}
          isOptional={true}
          errorMarginTop='8px'
          error={maxGlobalError}
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={source}
          onChange={handleChangeSource}
          placeholder='enterSpeciesGlobalSourceData'
          label='sourceGlobal'
          field='sourceGlobalData'
          infoTooltip='globalDataSourceHint'
          zIndex='1070'
          height='128px'
          disabled={disabled}
          isOptional={isIbaOnly}
          errorMarginTop='8px'
          error={sourceGlobalDataError}
        />
      </div>
      <div style={{ width: '100%' }}>
        <Textarea
          value={notes}
          onChange={handleChangeNotes}
          placeholder='enterNotesOnSpeciesGlobalData'
          label='notes'
          infoTooltip='globalDataNoteHint'
          zIndex='1070'
          height='128px'
          disabled={disabled}
          isOptional={true}
        />
      </div>
    </SpeciesDetailsContainer>
  )
}

export default SpeciesParameterGlobalDataContent

SpeciesParameterGlobalDataContent.propTypes = {
  parameter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    SiteAssesmentParameterTypeId: PropTypes.number,
    iba_only: PropTypes.bool,
    minGlobal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bestGlobal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxGlobal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceGlobalData: PropTypes.string,
    notes: PropTypes.string
  }).isRequired,
  siteSpecieParameterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  updateParameterList: PropTypes.func.isRequired,
  species: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    historyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  qualified: PropTypes.bool,
  assessmentParameter: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])])
  }),
  siteBestGlobal: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
  setSiteBestGlobal: PropTypes.func.isRequired,
  siteJustification: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setSiteJustification: PropTypes.func.isRequired
}

SpeciesParameterGlobalDataContent.defaultProps = {
  qualified: false,
  assessmentParameter: null,
  siteBestGlobal: '',
  siteJustification: false
}
